import { ChangeEvent, useRef, useState } from "react";
import { FormControl, MenuItem, TextField, styled as materialStyled } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import styled from "styled-components";
import HESPFormLabel from "../HESPFormLabel/HESPFormLabel";

export interface TimePeriod {
    start: string;
    end: string | null; // null means "now" (live mode with polling)
}

interface Props {
    value: TimePeriod;
    onChange: (period: TimePeriod) => void;
}

const Wrapper = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 16px;
    margin-bottom: 24px;
`;

const smallCss = {
    "& .MuiOutlinedInput-input": {
        padding: "8px 16px",
        fontSize: "14px",
    },
};

const CssTextField = materialStyled(TextField)(smallCss);

type PresetOption = "1h" | "6h" | "24h" | "7d" | "custom";

interface PresetConfig {
    label: string;
    getStart: () => moment.Moment;
}

const PRESETS: Record<Exclude<PresetOption, "custom">, PresetConfig> = {
    "1h": { label: "Last hour", getStart: () => moment().subtract(1, "hour") },
    "6h": { label: "Last 6 hours", getStart: () => moment().subtract(6, "hours") },
    "24h": { label: "Last 24 hours", getStart: () => moment().subtract(24, "hours") },
    "7d": { label: "Last 5 days", getStart: () => moment().subtract(5, "days") },
};

export default function TimePeriodSelector({ value, onChange }: Props) {
    const [preset, setPreset] = useState<PresetOption>("1h");
    const [customStart, setCustomStart] = useState<moment.Moment>(moment().subtract(1, "hour"));
    const [customEnd, setCustomEnd] = useState<moment.Moment>(moment());

    // Use refs to track latest values for callbacks
    const customStartRef = useRef(customStart);
    const customEndRef = useRef(customEnd);
    customStartRef.current = customStart;
    customEndRef.current = customEnd;

    const handlePresetChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newPreset = event.target.value as PresetOption;
        setPreset(newPreset);

        if (newPreset !== "custom") {
            const config = PRESETS[newPreset];
            onChange({
                start: config.getStart().toISOString(),
                end: null, // Live mode
            });
        } else {
            // When switching to custom, use the current custom date values
            onChange({
                start: customStartRef.current.toISOString(),
                end: customEndRef.current.toISOString(),
            });
        }
    };

    const handleCustomStartChange = (newValue: moment.Moment | null) => {
        if (newValue && newValue.isValid()) {
            setCustomStart(newValue);
            onChange({
                start: newValue.toISOString(),
                end: customEndRef.current.toISOString(),
            });
        }
    };

    const handleCustomEndChange = (newValue: moment.Moment | null) => {
        if (newValue && newValue.isValid()) {
            setCustomEnd(newValue);
            onChange({
                start: customStartRef.current.toISOString(),
                end: newValue.toISOString(),
            });
        }
    };

    const isLiveMode = value.end === null;

    return (
        <Wrapper>
            <div>
                <HESPFormLabel label="Time Period" />
                <FormControl>
                    <CssTextField
                        select
                        value={preset}
                        onChange={handlePresetChange}
                        size="small"
                        sx={{ minWidth: 150 }}
                    >
                        {Object.entries(PRESETS).map(([key, config]) => (
                            <MenuItem key={key} value={key}>
                                {config.label}
                            </MenuItem>
                        ))}
                        {/* <MenuItem value="custom">Custom range</MenuItem> */}
                    </CssTextField>
                </FormControl>
            </div>

            {preset === "custom" && (
                <>
                    <div>
                        <HESPFormLabel label="Start" />
                        <FormControl>
                            <DateTimePicker
                                format="YYYY/MM/DD HH:mm"
                                ampm={false}
                                value={customStart}
                                maxDateTime={customEnd}
                                onChange={handleCustomStartChange}
                                slotProps={{
                                    textField: { size: "small" },
                                }}
                            />
                        </FormControl>
                    </div>
                    <div>
                        <HESPFormLabel label="End" />
                        <FormControl>
                            <DateTimePicker
                                format="YYYY/MM/DD HH:mm"
                                ampm={false}
                                value={customEnd}
                                minDateTime={customStart}
                                maxDateTime={moment()}
                                onChange={handleCustomEndChange}
                                slotProps={{
                                    textField: { size: "small" },
                                }}
                            />
                        </FormControl>
                    </div>
                </>
            )}

            {isLiveMode && (
                <div style={{ fontSize: "12px", color: "#787878", alignSelf: "center" }}>Live updates enabled</div>
            )}
        </Wrapper>
    );
}
